import { useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { cx } from '@platform/core/lib/cx';
import type { IApiErrorPlain, IAttachment } from '@platform/core/types';
import { useFilesQuery } from '@platform/state/api';
import { ErrorBanner, Spinner } from '@platform/ui';
import { useConfirm } from '../form/confirm/useConfirm';
import { useRemoveFileMutation, useRenameFileMutation } from './api/recordTabsApi';
import { asPlainError, isUnsupported } from './files/asPlainError';
import { FilePreviewDialog } from './files/FilePreviewDialog';
import { FileRow } from './files/FileRow';
import { useFilePreview } from './files/useFilePreview';
import { useFileUploads } from './files/useFileUploads';
import { RecordIcon } from '../form/toolbar/RecordIcon';
import type { IRecordTabProps } from './IRecordTabProps';
import { useFileDownload } from './useFileDownload';
import { useRecordRefresh } from './useRecordRefresh';

/**
 * Вкладка «Файли» (UIUX field-types/file, forms/tabs; прототип v4): зона «Перетягніть файли сюди або клацніть, щоб завантажити» (кілька
 * одночасно, до 25 МБ; під час перетягування — акцентна), перелік до 860px: плитка типу, ім'я, розмір · хто · коли, «Завантажити» (з
 * авторизацією), перегляд зображень і PDF у вікні, «Перейменувати», «Видалити» з підтвердженням.
 */
export function FilesTab({ record }: IRecordTabProps) {
  const { t } = useTranslation();
  const { data, isLoading } = useFilesQuery(record.id);
  const uploads = useFileUploads(record);
  const download = useFileDownload();
  const viewer = useFilePreview();
  const refresh = useRecordRefresh(record);
  const [rename] = useRenameFileMutation();
  const [remove] = useRemoveFileMutation();
  const { confirm, dialog } = useConfirm();
  const [drag, setDrag] = useState(false);
  const [error, setError] = useState<IApiErrorPlain | null>(null);
  const canEdit = record.permissions?.edit !== false;
  const fail = (e: unknown) => { const plain = asPlainError(e); setError(isUnsupported(plain) ? { ...plain, message: t('recordPage.files.unsupported') } : plain); return false; };
  const onRename = (file: IAttachment, fileName: string) => rename({ recordId: record.id, fileId: file.id, fileName }).unwrap().then(() => true).catch(fail);
  const onDelete = (file: IAttachment) => void confirm({ title: t('recordPage.files.confirmDeleteTitle'), text: t('recordPage.files.confirmDelete', { name: file.fileName }), confirmLabel: t('recordPage.files.delete'), danger: true })
    .then((ok) => ok && remove({ recordId: record.id, rt: record.recordType, fileId: file.id }).unwrap().then(refresh).catch(fail));
  const onDrop = (e: DragEvent) => { e.preventDefault(); setDrag(false); void uploads.upload([...e.dataTransfer.files]); };
  if (isLoading) return <Spinner />;
  return (
    <div className="rtab-files">
      {record.permissions?.comment !== false && (
        <label className={cx('rtab-drop', drag && 'is-drag')} onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={onDrop}>
          <span className="rtab-drop__icon"><RecordIcon name="upload" size={22} /></span>
          <span className="rtab-drop__title">{uploads.busy ? t('recordPage.files.uploading') : t('recordPage.files.drop')}</span>
          <span className="rtab-drop__hint">{t('recordPage.files.dropHint')}</span>
          <input type="file" multiple hidden onChange={(e) => { void uploads.upload([...(e.target.files ?? [])]); e.target.value = ''; }} />
        </label>
      )}
      <ErrorBanner error={uploads.error ?? error ?? download.error} />
      <div className="rtab-box" aria-label={t('recordPage.files.list')}>
        {(data ?? []).map((f) => (
          <FileRow key={f.id} file={f} canEdit={canEdit} downloading={download.busyId === f.id} onPreview={(x) => void viewer.open(x)} onDownload={(x) => void download.download(x)} onRename={onRename} onDelete={onDelete} />
        ))}
        {!data?.length && <p className="rtab-files__empty">{t('recordPage.files.empty')}</p>}
      </div>
      {viewer.preview && <FilePreviewDialog preview={viewer.preview} onClose={viewer.close} onDownload={(x) => void download.download(x)} />}
      {dialog}
    </div>
  );
}
